// Main App component with routing
import { lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { phoneToWaDigits } from '@/utils/phoneUtils';
import { AuthProvider, AuthPathnameSync } from '@/providers/AuthProvider';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useRecordNavigationHistory } from '@/hooks/useNavigationHistory';
import { Home } from '@/pages/Home';
import { ClientesPortal } from '@/pages/ClientesPortal';
import { ClientesBilling } from '@/pages/ClientesBilling';
import { ClientesContratos } from '@/pages/ClientesContratos';
import { ClientesSuscripciones } from '@/pages/ClientesSuscripciones';
import { ClientesFacturas } from '@/pages/ClientesFacturas';
import { ClientesPresupuestos } from '@/pages/ClientesPresupuestos';
import { ClientesPerfil } from '@/pages/ClientesPerfil';
import { ClientesTramite } from '@/pages/ClientesTramite';
import { ClientesActividad } from '@/pages/ClientesActividad';
import { ClientesAyuda } from '@/pages/ClientesAyuda';
import { ClientesCitas } from '@/pages/ClientesCitas';
import ClientesSolicitudes from '@/pages/ClientesSolicitudes';
import { ClienteSetPassword } from '@/pages/ClienteSetPassword';
import { ClienteOnboarding } from '@/pages/ClienteOnboarding';
import { HiringFlow } from '@/pages/HiringFlow';
import { MandatePage } from '@/pages/MandatePage';
import { Login } from '@/pages/Login';
import { NotFound } from '@/pages/NotFound';
import { Expired } from '@/pages/Expired';
import { AdminLogin } from '@/pages/AdminLogin';
import { AdminLoginOtp } from '@/pages/AdminLoginOtp';
import { AdminDashboard } from '@/pages/AdminDashboard';
import { BorradorPDF } from '@/pages/BorradorPDF';
import { BorradorUriSuplementario } from '@/pages/BorradorUriSuplementario';
import { Colaboradores } from '@/pages/Colaboradores';
import { RegularizacionLanding } from '@/pages/RegularizacionLanding';
import { AbogadosLayout } from '@/pages/abogados/AbogadosLayout';
import { PitchLanding } from '@/pages/PitchLanding';
import { BusinessPlanPanel } from '@/pages/BusinessPlanPanel';
import { Closer } from '@/pages/Closer';
import { ColaboradoresAgentes } from '@/pages/ColaboradoresAgentes';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LazyLoadWrapper } from '@/components/common/LazyLoadWrapper';
import { CRMDeepLinkRedirect } from '@/components/CRM/CRMDeepLinkRedirect';
import { PerformanceMonitor } from '@/components/common/PerformanceMonitor';
import { Toaster } from 'sonner';

// Site corporativo (web institucional www.migro.es) - Lazy load
const SiteHome = lazy(() => import('@/pages/site/SiteHome'));
const SiteApp = lazy(() => import('@/pages/site/SiteApp'));
const SiteEmpresas = lazy(() => import('@/pages/site/SiteEmpresas'));
const SiteColaboradores = lazy(() => import('@/pages/site/SiteColaboradores'));
const SiteEquipo = lazy(() => import('@/pages/site/SiteEquipo'));

// Landing Pages - Lazy load (marketing, leads, legal)
const LandingHome = lazy(() => import('@/pages/landing/LandingHome'));
const LandingNacionalidad = lazy(() => import('@/pages/landing/LandingNacionalidad'));
const LeadIrregular = lazy(() => import('@/pages/landing/LeadIrregular'));
const CaptacionMadrid = lazy(() => import('@/pages/captacion/CaptacionMadrid'));
const ComercialLayout = lazy(() => import('@/pages/comercial/ComercialLayout'));
const ComercialDashboard = lazy(() => import('@/pages/comercial/ComercialDashboard'));
const ComercialLeads = lazy(() => import('@/pages/comercial/ComercialLeads'));
const LeadIrregularNew = lazy(() => import('@/pages/landing/LeadIrregularNew'));
const LeadIrregularPro = lazy(() => import('@/pages/landing/LeadIrregularPro'));
const MigroFastWizard = lazy(() => import('@/pages/MigroFastWizard'));
const MigroFastOperatorQueue = lazy(
  () => import('@/pages/admin/MigroFastOperatorQueue'),
);
const LeadDark = lazy(() => import('@/pages/landing/LeadDark'));
const LeadDarkReagrupacion = lazy(() => import('@/pages/landing/LeadDarkReagrupacion'));
const LeadDarkVisadoEstudios = lazy(() => import('@/pages/landing/LeadDarkVisadoEstudios'));
const LeadDarkNacionalidad = lazy(() => import('@/pages/landing/LeadDarkNacionalidad'));
const LeadVisadoEstudios = lazy(() => import('@/pages/landing/LeadVisadoEstudios'));
const LeadReagrupacion = lazy(() => import('@/pages/landing/LeadReagrupacion'));
const LandingConfirmation = lazy(() => import('@/pages/landing/LandingConfirmation'));
const LandingPrivacidad = lazy(() => import('@/pages/landing/LandingPrivacidad'));
const LandingCondiciones = lazy(() => import('@/pages/landing/LandingCondiciones'));
const LandingDataRemoval = lazy(() => import('@/pages/landing/LandingDataRemoval'));
const LandingSitemap = lazy(() => import('@/pages/landing/LandingSitemap'));

// Abogados site - lazy load (layout queda eager arriba)
const AbogadosHome = lazy(() => import('@/pages/abogados/HomePage'));
const AbogadosPlanesComparativa = lazy(() => import('@/pages/abogados/PlanesComparativa'));
const AbogadosPlanDetalle = lazy(() => import('@/pages/abogados/PlanDetalle'));
const AbogadosComoFunciona = lazy(() => import('@/pages/abogados/ComoFunciona'));
const AbogadosPiliPage = lazy(() => import('@/pages/abogados/PiliPage'));
const AbogadosLeadsPage = lazy(() => import('@/pages/abogados/LeadsPage'));
const AbogadosCasosExito = lazy(() => import('@/pages/abogados/CasosExito'));
const AbogadosRecursos = lazy(() => import('@/pages/abogados/Recursos'));
const AbogadosContacto = lazy(() => import('@/pages/abogados/Contacto'));

// CRM Pages - Lazy load componentes pesados
import { CRMLayout } from '@/components/CRM/CRMLayout';
// Lazy load de componentes pesados para reducir bundle inicial
const CRMDashboardPage = lazy(() => import('@/pages/CRMDashboardPage').then(m => ({ default: m.CRMDashboardPage })));
const CRMContactList = lazy(() => import('@/pages/CRMContactList').then(m => ({ default: m.CRMContactList })));
const CRMContactDetail = lazy(() => import('@/pages/CRMContactDetail').then(m => ({ default: m.CRMContactDetail })));
const ClienteDetalle = lazy(() => import('@/pages/ClienteDetalle').then(m => ({ default: m.ClienteDetalle })));
const CRMContactEdit = lazy(() => import('@/pages/CRMContactEdit').then(m => ({ default: m.CRMContactEdit })));
const CRMLeadList = lazy(() => import('@/pages/CRMLeadList').then(m => ({ default: m.CRMLeadList })));
const CRMLeadDetail = lazy(() => import('@/pages/CRMLeadDetail').then(m => ({ default: m.CRMLeadDetail })));
const CRMOpportunities = lazy(() => import('@/pages/CRMOpportunities').then(m => ({ default: m.CRMOpportunities })));
const CRMOpportunityDetail = lazy(() => import('@/pages/CRMOpportunityDetail').then(m => ({ default: m.CRMOpportunityDetail })));
const CRMOpportunitiesInbox = lazy(() => import('@/pages/CRMOpportunitiesInbox').then(m => ({ default: m.CRMOpportunitiesInbox })));
const CRMOpportunitiesMyClaims = lazy(() => import('@/pages/CRMOpportunitiesMyClaims').then(m => ({ default: m.CRMOpportunitiesMyClaims })));
const CRMOpportunitiesDigestSummary = lazy(() => import('@/pages/CRMOpportunitiesDigestSummary').then(m => ({ default: m.CRMOpportunitiesDigestSummary })));
const CRMOpportunityClaim = lazy(() => import('@/pages/CRMOpportunityClaim').then(m => ({ default: m.CRMOpportunityClaim })));
const CRMCaseAnalysis = lazy(() => import('@/pages/CRMCaseAnalysis').then(m => ({ default: m.CRMCaseAnalysis })));
const CRMTaskCalendar = lazy(() => import('@/pages/CRMTaskCalendar').then(m => ({ default: m.CRMTaskCalendar })));
const CRMSettings = lazy(() => import('@/pages/CRMSettings').then(m => ({ default: m.CRMSettings })));
const CRMAgentProfile = lazy(() => import('@/pages/CRMAgentProfile').then(m => ({ default: m.CRMAgentProfile })));
const CRMTaskTemplatesSettings = lazy(() => import('@/pages/CRMTaskTemplatesSettings').then(m => ({ default: m.CRMTaskTemplatesSettings })));
const CRMCustomFieldsSettings = lazy(() => import('@/pages/CRMCustomFieldsSettings').then(m => ({ default: m.CRMCustomFieldsSettings })));
const CRMTimezoneSettings = lazy(() => import('@/pages/CRMTimezoneSettings').then(m => ({ default: m.CRMTimezoneSettings })));
const CRMDelegationsSettings = lazy(() => import('@/pages/CRMDelegationsSettings').then(m => ({ default: m.CRMDelegationsSettings })));
const CRMResponsablesSettings = lazy(() => import('@/pages/CRMResponsablesSettings').then(m => ({ default: m.CRMResponsablesSettings })));
const CRMMigroSignatureSettings = lazy(() => import('@/pages/CRMMigroSignatureSettings').then(m => ({ default: m.CRMMigroSignatureSettings })));
const CRMActions = lazy(() => import('@/pages/CRMActions').then(m => ({ default: m.CRMActions })));
const CRMActivityCenter = lazy(() => import('@/pages/CRMActivityCenter').then(m => ({ default: m.CRMActivityCenter })));
const SeguimientosBoard = lazy(() => import('@/pages/SeguimientosBoard').then(m => ({ default: m.SeguimientosBoard })));
const CRMExpedientesList = lazy(() => import('@/pages/CRMExpedientesList').then(m => ({ default: m.CRMExpedientesList })));
const CRMExpedienteCreate = lazy(() => import('@/pages/CRMExpedienteCreate').then(m => ({ default: m.CRMExpedienteCreate })));
const ExpedienteDetailV2 = lazy(() => import('@/pages/expedientes/ExpedienteDetailV2').then(m => ({ default: m.ExpedienteDetailV2 })));
const ExpedienteV2Preview = lazy(() => import('@/pages/expedientes/ExpedienteV2Preview').then(m => ({ default: m.ExpedienteV2Preview })));
const ExpedienteRealPreview = lazy(() => import('@/pages/expedientes/ExpedienteRealPreview').then(m => ({ default: m.ExpedienteRealPreview })));
const CRMIncidencias = lazy(() => import('@/pages/CRMIncidencias').then(m => ({ default: m.default })));
const CRMIncidenciaDetail = lazy(() => import('@/pages/CRMIncidenciaDetail').then(m => ({ default: m.default })));
const CRMIncidenciaCreate = lazy(() => import('@/pages/CRMIncidenciaCreate').then(m => ({ default: m.default })));
const CRMIncidenciaDashboard = lazy(() => import('@/pages/CRMIncidenciaDashboard').then(m => ({ default: m.default })));
const CRMFAQsAdmin = lazy(() => import('@/pages/CRMFAQsAdmin').then(m => ({ default: m.default })));
const ClientesIncidencias = lazy(() => import('@/pages/ClientesIncidencias').then(m => ({ default: m.default })));
const ClienteIncidenciaDetail = lazy(() => import('@/pages/ClienteIncidenciaDetail').then(m => ({ default: m.default })));
const ClienteIncidenciaCreate = lazy(() => import('@/pages/ClienteIncidenciaCreate').then(m => ({ default: m.default })));
const CRMCallHandler = lazy(() => import('@/pages/CRMCallHandler').then(m => ({ default: m.CRMCallHandler })));
const CRMCallsArchive = lazy(() => import('@/pages/CRMCallsArchive').then(m => ({ default: m.CRMCallsArchive })));
const CRMWorkSession = lazy(() => import('@/pages/CRMWorkSession').then(m => ({ default: m.default })));
const TelnyxCallTestPage = lazy(() => import('@/pages/TelnyxCallTestPage').then(m => ({ default: m.TelnyxCallTestPage })));
const TelnyxDualCallTestPage = lazy(() => import('@/pages/TelnyxDualCallTestPage').then(m => ({ default: m.TelnyxDualCallTestPage })));
const AgentPanel = lazy(() => import('@/pages/webrtc-test/AgentPanel').then(m => ({ default: m.AgentPanel })));
const BotPanel = lazy(() => import('@/pages/webrtc-test/BotPanel').then(m => ({ default: m.BotPanel })));
const CRMTaskDetail = lazy(() => import('@/pages/CRMTaskDetail').then(m => ({ default: m.CRMTaskDetail })));
const CRMTasks = lazy(() => import('@/pages/CRMTasks').then(m => ({ default: m.CRMTasks })));
const CRMNotes = lazy(() => import('@/pages/CRMNotes').then(m => ({ default: m.CRMNotes })));
const CRMContracts = lazy(() => import('@/pages/CRMContracts').then(m => ({ default: m.CRMContracts })));
const CRMAgentJournal = lazy(() => import('@/pages/CRMAgentJournal').then(m => ({ default: m.CRMAgentJournal })));
const CRMOnboardingPage = lazy(() => import('@/pages/CRMOnboardingPage').then(m => ({ default: m.CRMOnboardingPage })));
const CRMHistoryPage = lazy(() => import('@/pages/CRMHistoryPage').then(m => ({ default: m.CRMHistoryPage })));
// Motor de funnels (marketing automation) — bajo /admin/funnels
const CRMFunnels = lazy(() => import('@/pages/CRMFunnels').then(m => ({ default: m.CRMFunnels })));
const CRMFunnelDetail = lazy(() => import('@/pages/CRMFunnelDetail').then(m => ({ default: m.CRMFunnelDetail })));
const CRMFunnelEditor = lazy(() => import('@/pages/CRMFunnelEditor').then(m => ({ default: m.CRMFunnelEditor })));
const GoogleCalendarCallback = lazy(() => import('@/pages/GoogleCalendarCallback').then(m => ({ default: m.GoogleCalendarCallback })));

// CRM Appointments Module
import { CRMAppointments } from '@/pages/CRMAppointments';
import { CRMAppointmentDetail } from '@/pages/CRMAppointmentDetail';
import { CRMCalendar } from '@/pages/CRMCalendar';
import { CRMDelegations } from '@/pages/CRMDelegations';

// CRM Contracts New Module (agentes)
import { CRMContractsListNew } from '@/pages/CRMContractsListNew';
import { CRMContractCreateNew } from '@/pages/CRMContractCreateNew';
import { CRMContractDetailNew } from '@/pages/CRMContractDetailNew';
import { CRMAnnexTemplatesNew } from '@/pages/CRMAnnexTemplatesNew';

// Billing Module - Lazy load
const CRMPresupuestosList = lazy(() => import('@/pages/CRMPresupuestosList').then(m => ({ default: m.CRMPresupuestosList })));
const CRMPresupuestoCreate = lazy(() => import('@/pages/CRMPresupuestoCreate').then(m => ({ default: m.CRMPresupuestoCreate })));
const CRMPresupuestoDetail = lazy(() => import('@/pages/CRMPresupuestoDetail').then(m => ({ default: m.CRMPresupuestoDetail })));
const CRMFacturasList = lazy(() => import('@/pages/CRMFacturasList').then(m => ({ default: m.CRMFacturasList })));
const CRMFacturaCreate = lazy(() => import('@/pages/CRMFacturaCreate').then(m => ({ default: m.CRMFacturaCreate })));
const CRMFacturaDetail = lazy(() => import('@/pages/CRMFacturaDetail').then(m => ({ default: m.CRMFacturaDetail })));
const PublicQuoteView = lazy(() => import('@/pages/PublicQuoteView').then(m => ({ default: m.PublicQuoteView })));
const PublicBooking = lazy(() => import('@/pages/PublicBooking'));
const AgendarCall = lazy(() => import('@/pages/AgendarCall'));
const CancelarCita = lazy(() => import('@/pages/CancelarCita'));

// Admin Pages - Lazy load componentes pesados
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers').then(m => ({ default: m.AdminUsers })));
const AdminUserDetail = lazy(() => import('@/pages/admin/AdminUserDetail').then(m => ({ default: m.AdminUserDetail })));
const AdminUserCreate = lazy(() => import('@/pages/admin/AdminUserCreate').then(m => ({ default: m.AdminUserCreate })));
// Captación Madrid (admin) — módulo /admin/captacion
const AdminCaptacionLayout = lazy(() => import('@/pages/admin/captacion/AdminCaptacionLayout'));
const AdminCaptacionComerciales = lazy(() => import('@/pages/admin/captacion/AdminCaptacionComerciales'));
const AdminCaptacionComercialNew = lazy(() => import('@/pages/admin/captacion/AdminCaptacionComercialNew'));
const AdminCaptacionComercialDetail = lazy(() => import('@/pages/admin/captacion/AdminCaptacionComercialDetail'));
const AdminCaptacionNegocios = lazy(() => import('@/pages/admin/captacion/AdminCaptacionNegocios'));
const AdminCaptacionNegocioNew = lazy(() => import('@/pages/admin/captacion/AdminCaptacionNegocioNew'));
const AdminCaptacionNegocioDetail = lazy(() => import('@/pages/admin/captacion/AdminCaptacionNegocioDetail'));
const AdminCaptacionCodigos = lazy(() => import('@/pages/admin/captacion/AdminCaptacionCodigos'));
const AdminCaptacionCodigoNew = lazy(() => import('@/pages/admin/captacion/AdminCaptacionCodigoNew'));
const AdminCaptacionFunnel = lazy(() => import('@/pages/admin/captacion/AdminCaptacionFunnel'));
const AdminAuditLogs = lazy(() => import('@/pages/admin/AdminAuditLogs').then(m => ({ default: m.AdminAuditLogs })));
const AdminPili = lazy(() => import('@/pages/admin/AdminPili').then(m => ({ default: m.AdminPili })));
const AdminMCPAds = lazy(() => import('@/pages/admin/AdminMCPAds').then(m => ({ default: m.AdminMCPAds })));
const AdminVoiceStudio = lazy(() => import('@/pages/admin/AdminVoiceStudio').then(m => ({ default: m.AdminVoiceStudio })));
// PiliLab (portado desde Desarrollo_old3 — voice/recorder/sim/eval lab)
const PiliLabLayout = lazy(() => import('@/pages/admin/pili-lab/PiliLabLayout').then(m => ({ default: m.PiliLabLayout })));
const PiliLabDashboard = lazy(() => import('@/pages/admin/pili-lab/PiliLabDashboard').then(m => ({ default: m.PiliLabDashboard })));
const PiliLabRecord = lazy(() => import('@/pages/admin/pili-lab/PiliLabRecord').then(m => ({ default: m.PiliLabRecord })));
const PiliLabVoices = lazy(() => import('@/pages/admin/pili-lab/PiliLabVoices').then(m => ({ default: m.PiliLabVoices })));
const PiliLabVoiceDetail = lazy(() => import('@/pages/admin/pili-lab/PiliLabVoiceDetail').then(m => ({ default: m.PiliLabVoiceDetail })));
const PiliLabSimulate = lazy(() => import('@/pages/admin/pili-lab/PiliLabSimulate').then(m => ({ default: m.PiliLabSimulate })));
const PiliLabDemoCall = lazy(() => import('@/pages/admin/pili-lab/PiliLabDemoCall').then(m => ({ default: m.PiliLabDemoCall })));
const PiliLabPrompts = lazy(() => import('@/pages/admin/pili-lab/PiliLabPrompts').then(m => ({ default: m.PiliLabPrompts })));
const PiliLabEvals = lazy(() => import('@/pages/admin/pili-lab/PiliLabEvals').then(m => ({ default: m.PiliLabEvals })));
const PiliLabEvalDetail = lazy(() => import('@/pages/admin/pili-lab/PiliLabEvalDetail').then(m => ({ default: m.PiliLabEvalDetail })));
const PiliLabAudit = lazy(() => import('@/pages/admin/pili-lab/PiliLabAudit').then(m => ({ default: m.PiliLabAudit })));
// PiliLab Voice Tabs (sprint 2 fase A — read-only)
const PiliLabSpeeches = lazy(() => import('@/pages/admin/pili-lab/PiliLabSpeeches').then(m => ({ default: m.PiliLabSpeeches })));
const PiliLabRulebook = lazy(() => import('@/pages/admin/pili-lab/PiliLabRulebook').then(m => ({ default: m.PiliLabRulebook })));
const PiliLabPreview = lazy(() => import('@/pages/admin/pili-lab/PiliLabPreview').then(m => ({ default: m.PiliLabPreview })));
const PiliLabQueue = lazy(() => import('@/pages/admin/pili-lab/PiliLabQueue').then(m => ({ default: m.PiliLabQueue })));
const PiliLabPatterns = lazy(() => import('@/pages/admin/pili-lab/PiliLabPatterns').then(m => ({ default: m.PiliLabPatterns })));
const AdminConversations = lazy(() => import('@/pages/admin/AdminConversations').then(m => ({ default: m.AdminConversations })));
const AdminConversationDetail = lazy(() => import('@/pages/admin/AdminConversationDetail').then(m => ({ default: m.AdminConversationDetail })));
const AdminContractDetail = lazy(() => import('@/pages/admin/AdminContractDetail').then(m => ({ default: m.AdminContractDetail })));
const AdminContractCreate = lazy(() => import('@/pages/admin/AdminContractCreate').then(m => ({ default: m.AdminContractCreate })));
const AdminCallTypes = lazy(() => import('@/pages/admin/AdminCallTypes').then(m => ({ default: m.AdminCallTypes })));
const AdminTracingDashboard = lazy(() => import('@/pages/admin/AdminTracingDashboard').then(m => ({ default: m.AdminTracingDashboard })));
const AdminRoutePermissions = lazy(() => import('@/pages/admin/AdminRoutePermissions').then(m => ({ default: m.AdminRoutePermissions })));
const AdminOpportunities = lazy(() => import('@/pages/admin/AdminOpportunities').then(m => ({ default: m.AdminOpportunities })));
const AdminReleaseFreeCandidates = lazy(() => import('@/pages/admin/AdminReleaseFreeCandidates').then(m => ({ default: m.AdminReleaseFreeCandidates })));
const AdminStripeSearch = lazy(() => import('@/pages/admin/AdminStripeSearch').then(m => ({ default: m.AdminStripeSearch })));
const AdminContabilidad = lazy(() => import('@/pages/admin/AdminContabilidad').then(m => ({ default: m.AdminContabilidad })));
const AdminEntityRedirect = lazy(() => import('@/pages/admin/AdminEntityRedirect').then(m => ({ default: m.AdminEntityRedirect })));
const ApproveHiringCode = lazy(() => import('@/pages/admin/ApproveHiringCode').then(m => ({ default: m.ApproveHiringCode })));

// Admin Agent Orchestrator
const AdminAgents = lazy(() => import('@/pages/admin/agents/AdminAgents').then(m => ({ default: m.AdminAgents })));
const AdminAgentNew = lazy(() => import('@/pages/admin/agents/AdminAgentNew').then(m => ({ default: m.AdminAgentNew })));
const AdminAgentEdit = lazy(() => import('@/pages/admin/agents/AdminAgentEdit').then(m => ({ default: m.AdminAgentEdit })));
const AdminAgentDetail = lazy(() => import('@/pages/admin/agents/AdminAgentDetail').then(m => ({ default: m.AdminAgentDetail })));
const AdminAgentExecute = lazy(() => import('@/pages/admin/agents/AdminAgentExecute').then(m => ({ default: m.AdminAgentExecute })));
const AdminAgentExecutions = lazy(() => import('@/pages/admin/agents/AdminAgentExecutions').then(m => ({ default: m.AdminAgentExecutions })));
const AdminAgentsExecutions = lazy(() => import('@/pages/admin/agents/AdminAgentsExecutions').then(m => ({ default: m.AdminAgentsExecutions })));
const AdminAgentsPipelines = lazy(() => import('@/pages/admin/agents/AdminAgentsPipelines').then(m => ({ default: m.AdminAgentsPipelines })));
const AdminPipelineNew = lazy(() => import('@/pages/admin/agents/AdminPipelineNew').then(m => ({ default: m.AdminPipelineNew })));
const AdminPipelineDetail = lazy(() => import('@/pages/admin/agents/AdminPipelineDetail').then(m => ({ default: m.AdminPipelineDetail })));
const AdminPipelineEdit = lazy(() => import('@/pages/admin/agents/AdminPipelineEdit').then(m => ({ default: m.AdminPipelineEdit })));
const AdminPipelineRun = lazy(() => import('@/pages/admin/agents/AdminPipelineRun').then(m => ({ default: m.AdminPipelineRun })));
const AdminPipelineRuns = lazy(() => import('@/pages/admin/agents/AdminPipelineRuns').then(m => ({ default: m.AdminPipelineRuns })));
const AdminPipelineRunDetail = lazy(() => import('@/pages/admin/agents/AdminPipelineRunDetail').then(m => ({ default: m.AdminPipelineRunDetail })));
const AdminExecutionDetail = lazy(() => import('@/pages/admin/agents/AdminExecutionDetail').then(m => ({ default: m.AdminExecutionDetail })));

/** Redirige /c/* (short URL) a /contratacion/* para compatibilidad con enlaces tipo crm.migro.es/c/FSHV8 */
function ShortUrlToContratacionRedirect() {
  const { '*': rest } = useParams<{ '*': string }>();
  const target = rest ? `/contratacion/${rest}` : '/';
  return <Navigate to={target} replace />;
}

// La ficha v2 doc-céntrica es la ÚNICA ficha del expediente (card 75). La ruta legacy /ia
// (ExpedienteAIModule) redirige a la ficha para mantener vivos los enlaces antiguos.
function ExpedienteIaRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/crm/expedientes/${id ?? ''}`} replace />;
}

/** Vanity URL /grupo → grupo de WhatsApp de Migro */
function WhatsAppGroupRedirect() {
  useEffect(() => {
    window.location.replace('https://chat.whatsapp.com/Dmgi9NMvzs50BUldb7pRXF?mode=gi_t');
  }, []);
  return null;
}

/**
 * Vanity URL /whatsapp[/:to] → chat directo wa.me.
 *
 * `:to` debe ser dígitos (E.164 sin `+`). Si el valor no normaliza o no
 * viene, redirige al WhatsApp de Brenda (fallback acordado con humano,
 * 2026-05-21). El backend del CRM compone /whatsapp/<numero> dinámicamente
 * al enviar follow-up emails con el teléfono del responsible.
 */
const WHATSAPP_FALLBACK_DIGITS = '34614813568'; // Brenda
function WhatsAppRedirect() {
  const { to } = useParams<{ to?: string }>();
  useEffect(() => {
    const digits = phoneToWaDigits(to) ?? WHATSAPP_FALLBACK_DIGITS;
    window.location.replace(`https://wa.me/${digits}`);
  }, [to]);
  return null;
}

function AppContent() {
  // Activar refresh automático de tokens
  useTokenRefresh();
  // Grabar cada visita en cookie para que "Volver" use el historial real
  useRecordNavigationHistory();
  // Actualizar título de página automáticamente según la ruta
  usePageTitle();

  return (
    <Routes>
          {/* Public routes - NO requieren autenticación */}
          {/* Landing / Marketing home */}
          <Route path="/" element={<LazyLoadWrapper fallback="spinner"><SiteHome /></LazyLoadWrapper>} />
          <Route path="/app" element={<LazyLoadWrapper fallback="spinner"><SiteApp /></LazyLoadWrapper>} />
          <Route path="/empresas" element={<LazyLoadWrapper fallback="spinner"><SiteEmpresas /></LazyLoadWrapper>} />
          <Route path="/equipo" element={<LazyLoadWrapper fallback="spinner"><SiteEquipo /></LazyLoadWrapper>} />
          <Route path="/landing-old" element={<LazyLoadWrapper fallback="spinner"><LandingHome /></LazyLoadWrapper>} />
          {/* Vanity URL pública → grupo de WhatsApp de Migro */}
          <Route path="/grupo" element={<WhatsAppGroupRedirect />} />
          {/* Vanity URL pública → chat directo de WhatsApp (asesor responsable o fallback Brenda) */}
          <Route path="/whatsapp" element={<WhatsAppRedirect />} />
          <Route path="/whatsapp/:to" element={<WhatsAppRedirect />} />
          {/* Código de contratación (antigua home) */}
          <Route path="/contratacion" element={<Home />} />
          {/* Portal cliente (slug) */}
          <Route path="/clientes" element={<ClientesPortal />} />
          <Route path="/clientes/activar/:token" element={<ClienteSetPassword />} />
          <Route path="/clientes/onboarding" element={<ProtectedRoute><ClienteOnboarding /></ProtectedRoute>} />
          <Route path="/clientes/billing" element={<ClientesBilling />} />
          <Route path="/clientes/contratos" element={<ProtectedRoute><ClientesContratos /></ProtectedRoute>} />
          <Route path="/clientes/suscripciones" element={<ProtectedRoute><ClientesSuscripciones /></ProtectedRoute>} />
          <Route path="/clientes/presupuestos" element={<ProtectedRoute><ClientesPresupuestos /></ProtectedRoute>} />
          <Route path="/clientes/facturas" element={<ProtectedRoute><ClientesFacturas /></ProtectedRoute>} />
          <Route path="/clientes/perfil" element={<ProtectedRoute><ClientesPerfil /></ProtectedRoute>} />
          <Route path="/clientes/tramite" element={<ProtectedRoute><ClientesTramite /></ProtectedRoute>} />
          <Route path="/clientes/solicitudes" element={<ProtectedRoute><ClientesSolicitudes /></ProtectedRoute>} />
          <Route path="/clientes/actividad" element={<ProtectedRoute><ClientesActividad /></ProtectedRoute>} />
          <Route path="/clientes/ayuda" element={<ProtectedRoute><ClientesAyuda /></ProtectedRoute>} />
          <Route path="/clientes/incidencias" element={<ProtectedRoute><LazyLoadWrapper fallback="skeleton" skeletonCount={3}><ClientesIncidencias /></LazyLoadWrapper></ProtectedRoute>} />
          <Route path="/clientes/incidencias/nueva" element={<ProtectedRoute><LazyLoadWrapper fallback="spinner"><ClienteIncidenciaCreate /></LazyLoadWrapper></ProtectedRoute>} />
          <Route path="/clientes/incidencias/:id" element={<ProtectedRoute><LazyLoadWrapper fallback="spinner"><ClienteIncidenciaDetail /></LazyLoadWrapper></ProtectedRoute>} />
          <Route path="/clientes/citas" element={<ProtectedRoute><ClientesCitas /></ProtectedRoute>} />
          {/* Booking público (tipo Calendly, sin autenticación) */}
          <Route path="/booking/:token" element={<LazyLoadWrapper fallback="spinner"><PublicBooking /></LazyLoadWrapper>} />
          {/* Agendar/reagendar llamada (lead llega desde el CTA de los emails de seguimiento) */}
          <Route path="/agendar" element={<LazyLoadWrapper fallback="spinner"><AgendarCall /></LazyLoadWrapper>} />
          {/* MigroFast: wizard público de solicitud ESTA / UK ETA */}
          <Route path="/migrofast/:product" element={<LazyLoadWrapper fallback="spinner"><MigroFastWizard /></LazyLoadWrapper>} />
          {/* MigroFast: cola de operador (admin/CRM) */}
          <Route path="/crm/migrofast-cola" element={<ProtectedRoute allowedRoles={['agent', 'lawyer', 'admin', 'superuser']}><LazyLoadWrapper fallback="spinner"><MigroFastOperatorQueue /></LazyLoadWrapper></ProtectedRoute>} />
          {/* Cancelación pública de cita (lead llega desde el botón "Cancelar" de los emails) */}
          <Route path="/agendar/cancelar" element={<LazyLoadWrapper fallback="spinner"><CancelarCita /></LazyLoadWrapper>} />
          {/* Presupuesto público (sin autenticación, acceso por token) */}
          <Route path="/presupuesto/:token" element={<LazyLoadWrapper fallback="spinner"><PublicQuoteView /></LazyLoadWrapper>} />
          {/* Módulo mandato: URI propia /mandato/:code (independiente del flujo de contratación) */}
          <Route path="/mandato/:code" element={<MandatePage />} />
          {/* Short URL: /c/* → /contratacion/* (ej. crm.migro.es/c/FSHV8 → /contratacion/FSHV8) */}
          <Route path="/c/*" element={<ShortUrlToContratacionRedirect />} />
          {/* Ruta parameterizada DEBE ir al final para no capturar rutas específicas */}
          <Route path="/clientes/:code" element={<HiringFlow />} />
          <Route path="/contratacion/:code" element={<HiringFlow />} />
          {import.meta.env.DEV && (
            <Route path="/dev/expediente-v2" element={<LazyLoadWrapper fallback="spinner"><ExpedienteV2Preview /></LazyLoadWrapper>} />
          )}
          {import.meta.env.DEV && (
            <Route path="/dev/expediente-real" element={<LazyLoadWrapper fallback="spinner"><ExpedienteRealPreview /></LazyLoadWrapper>} />
          )}
          <Route path="/hiring/:code" element={<HiringFlow />} /> {/* Alias en inglés */}
          <Route path="/expirado" element={<Expired />} />
          <Route path="/404" element={<NotFound />} />
          <Route path="/privacidad" element={<LazyLoadWrapper fallback="spinner"><LandingPrivacidad /></LazyLoadWrapper>} />
          <Route path="/privacy" element={<LazyLoadWrapper fallback="spinner"><LandingPrivacidad /></LazyLoadWrapper>} /> {/* Alias en inglés */}
          <Route path="/borrador" element={<BorradorPDF />} />
          <Route path="/borrador/:slug" element={<BorradorUriSuplementario />} />
          <Route path="/colaboradores" element={<LazyLoadWrapper fallback="spinner"><SiteColaboradores /></LazyLoadWrapper>} />
          <Route path="/colaboradores-old" element={<Colaboradores />} />
          <Route path="/colaboradores-agentes" element={<ColaboradoresAgentes />} />
          <Route path="/closer" element={<Closer />} />
          <Route path="/regularizacion" element={<RegularizacionLanding />} />
          <Route path="/regularizacion-v2" element={<Navigate to="/regularizacion" replace />} />
          <Route path="/abogados" element={<AbogadosLayout />}>
            <Route index element={<LazyLoadWrapper fallback="spinner"><AbogadosHome /></LazyLoadWrapper>} />
            <Route path="planes" element={<LazyLoadWrapper fallback="spinner"><AbogadosPlanesComparativa /></LazyLoadWrapper>} />
            <Route path="planes/:id" element={<LazyLoadWrapper fallback="spinner"><AbogadosPlanDetalle /></LazyLoadWrapper>} />
            <Route path="como-funciona" element={<LazyLoadWrapper fallback="spinner"><AbogadosComoFunciona /></LazyLoadWrapper>} />
            <Route path="pili" element={<LazyLoadWrapper fallback="spinner"><AbogadosPiliPage /></LazyLoadWrapper>} />
            <Route path="leads" element={<LazyLoadWrapper fallback="spinner"><AbogadosLeadsPage /></LazyLoadWrapper>} />
            <Route path="casos-exito" element={<LazyLoadWrapper fallback="spinner"><AbogadosCasosExito /></LazyLoadWrapper>} />
            <Route path="recursos" element={<LazyLoadWrapper fallback="spinner"><AbogadosRecursos /></LazyLoadWrapper>} />
            <Route path="contacto" element={<LazyLoadWrapper fallback="spinner"><AbogadosContacto /></LazyLoadWrapper>} />
          </Route>
          <Route path="/pitch" element={<PitchLanding />} />
          <Route path="/panel" element={<ProtectedRoute requireAdmin><BusinessPlanPanel /></ProtectedRoute>} />

          {/* Landing Pages - Lead capture & marketing */}
          <Route path="/nacionalidad" element={<LazyLoadWrapper fallback="spinner"><LandingNacionalidad /></LazyLoadWrapper>} />
          <Route path="/leads/irregulares" element={<LazyLoadWrapper fallback="spinner"><LeadIrregular /></LazyLoadWrapper>} />
          {/* Alias singular: anuncios de Meta y nav del sitio enlazan /leads/irregular (sin landing propia).
              Sin esto cae en el catch-all /leads/:id → CRM → login de admin. */}
          <Route path="/leads/irregular" element={<Navigate to="/leads/irregulares" replace />} />
          {/* Captación física Madrid: QR de locutorios → landing irregulares con atribución */}
          <Route path="/qr/:code" element={<LazyLoadWrapper fallback="spinner"><CaptacionMadrid /></LazyLoadWrapper>} />
          {/* Portal comercial capado (rol comercial): solo SUS leads + funnel */}
          <Route
            path="/comercial"
            element={
              <ProtectedRoute allowedRoles={['comercial']} useDynamicPermissions={false}>
                <ComercialLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<LazyLoadWrapper fallback="spinner"><ComercialDashboard /></LazyLoadWrapper>} />
            <Route path="leads" element={<LazyLoadWrapper fallback="spinner"><ComercialLeads /></LazyLoadWrapper>} />
          </Route>
          <Route path="/leads/irregulares-new" element={<LazyLoadWrapper fallback="spinner"><LeadIrregularNew /></LazyLoadWrapper>} />
          <Route path="/leads/irregulares-pro" element={<LazyLoadWrapper fallback="spinner"><LeadIrregularPro /></LazyLoadWrapper>} />
          <Route path="/leads/dark" element={<LazyLoadWrapper fallback="spinner"><LeadDark /></LazyLoadWrapper>} />
          <Route path="/leads/dark/irregular" element={<LazyLoadWrapper fallback="spinner"><LeadDark /></LazyLoadWrapper>} />
          <Route path="/leads/dark/reagrupacion" element={<LazyLoadWrapper fallback="spinner"><LeadDarkReagrupacion /></LazyLoadWrapper>} />
          <Route path="/leads/dark/visado-estudios" element={<LazyLoadWrapper fallback="spinner"><LeadDarkVisadoEstudios /></LazyLoadWrapper>} />
          <Route path="/leads/dark/nacionalidad" element={<LazyLoadWrapper fallback="spinner"><LeadDarkNacionalidad /></LazyLoadWrapper>} />
          <Route path="/leads/visado-estudios" element={<LazyLoadWrapper fallback="spinner"><LeadVisadoEstudios /></LazyLoadWrapper>} />
          <Route path="/leads/reagrupacion-familiar" element={<LazyLoadWrapper fallback="spinner"><LeadReagrupacion /></LazyLoadWrapper>} />
          <Route path="/confirmacion/:service" element={<LazyLoadWrapper fallback="spinner"><LandingConfirmation /></LazyLoadWrapper>} />
          {/* Legal pages (landing) */}
          <Route path="/condiciones" element={<LazyLoadWrapper fallback="spinner"><LandingCondiciones /></LazyLoadWrapper>} />
          <Route path="/data-removal" element={<LazyLoadWrapper fallback="spinner"><LandingDataRemoval /></LazyLoadWrapper>} />
          <Route path="/sitemap" element={<LazyLoadWrapper fallback="spinner"><LandingSitemap /></LazyLoadWrapper>} />
          <Route path="/pili" element={<AdminPili />} />
          
          {/* Servicio de contratación y firma - RUTAS ANTIGUAS (mantener para compatibilidad) */}
          <Route path="/contrato-old/login" element={<AdminLogin />} />
          <Route path="/contrato-old/dashboard" element={<AdminDashboard />} />
          <Route path="/contrato-old" element={<AdminLogin />} />
          
          {/* Rutas de autenticación unificadas */}
          <Route path="/auth/login" element={<AdminLogin />} />
          <Route path="/auth/login-otp" element={<AdminLoginOtp />} />

          {/* Deep links CRM (backend redirige desde correos de asignación) → redirigen a /crm/... */}
          <Route path="/tasks/:id" element={<CRMDeepLinkRedirect segment="tasks" />} />
          <Route path="/calls/:id" element={<CRMDeepLinkRedirect segment="calls" />} />
          <Route path="/opportunities/:id" element={<CRMDeepLinkRedirect segment="opportunities" />} />
          <Route path="/contacts/:id" element={<CRMDeepLinkRedirect segment="contacts" />} />
          <Route path="/leads/:id" element={<CRMDeepLinkRedirect segment="leads" />} />
          
          {/* Servicio de contratación y firma - Dashboard */}
          <Route path="/contrato/dashboard" element={<AdminDashboard />} />
          
          {/* Ruta pública de aprobación de hiring code (no requiere autenticación) */}
          <Route 
            path="/admin/approve-hiring-code" 
            element={<LazyLoadWrapper fallback="spinner"><ApproveHiringCode /></LazyLoadWrapper>} 
          />
          
          {/* Admin Module - Panel de administración */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<LazyLoadWrapper fallback="spinner"><AdminDashboardPage /></LazyLoadWrapper>} />
            <Route path="dashboard" element={<LazyLoadWrapper fallback="spinner"><AdminDashboardPage /></LazyLoadWrapper>} />
            <Route path="users" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><AdminUsers /></LazyLoadWrapper>} />
            <Route path="users/create" element={<LazyLoadWrapper fallback="spinner"><AdminUserCreate /></LazyLoadWrapper>} />
            <Route path="users/:id" element={<LazyLoadWrapper fallback="spinner"><AdminUserDetail /></LazyLoadWrapper>} />
            {/* Captación Madrid — CRUD comerciales/locutorios/códigos */}
            <Route path="captacion" element={<LazyLoadWrapper fallback="spinner"><AdminCaptacionLayout /></LazyLoadWrapper>}>
              <Route index element={<Navigate to="comerciales" replace />} />
              <Route path="comerciales" element={<LazyLoadWrapper fallback="spinner"><AdminCaptacionComerciales /></LazyLoadWrapper>} />
              <Route path="comerciales/nuevo" element={<LazyLoadWrapper fallback="spinner"><AdminCaptacionComercialNew /></LazyLoadWrapper>} />
              <Route path="comerciales/:id" element={<LazyLoadWrapper fallback="spinner"><AdminCaptacionComercialDetail /></LazyLoadWrapper>} />
              <Route path="negocios" element={<LazyLoadWrapper fallback="spinner"><AdminCaptacionNegocios /></LazyLoadWrapper>} />
              <Route path="negocios/nuevo" element={<LazyLoadWrapper fallback="spinner"><AdminCaptacionNegocioNew /></LazyLoadWrapper>} />
              <Route path="negocios/:id" element={<LazyLoadWrapper fallback="spinner"><AdminCaptacionNegocioDetail /></LazyLoadWrapper>} />
              <Route path="codigos" element={<LazyLoadWrapper fallback="spinner"><AdminCaptacionCodigos /></LazyLoadWrapper>} />
              <Route path="codigos/nuevo" element={<LazyLoadWrapper fallback="spinner"><AdminCaptacionCodigoNew /></LazyLoadWrapper>} />
              <Route path="funnel" element={<LazyLoadWrapper fallback="spinner"><AdminCaptacionFunnel /></LazyLoadWrapper>} />
            </Route>
            <Route path="audit-logs" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><AdminAuditLogs /></LazyLoadWrapper>} />
            <Route path="entities/:type/:id" element={<LazyLoadWrapper fallback="spinner"><AdminEntityRedirect /></LazyLoadWrapper>} />
            <Route path="conversations" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><AdminConversations /></LazyLoadWrapper>} />
            <Route path="conversations/:id" element={<LazyLoadWrapper fallback="spinner"><AdminConversationDetail /></LazyLoadWrapper>} />
            <Route path="call-types" element={<LazyLoadWrapper fallback="spinner"><AdminCallTypes /></LazyLoadWrapper>} />
            <Route path="route-permissions" element={<LazyLoadWrapper fallback="spinner"><AdminRoutePermissions /></LazyLoadWrapper>} />
            <Route path="tracing" element={<LazyLoadWrapper fallback="spinner"><AdminTracingDashboard /></LazyLoadWrapper>} />
            <Route path="voice-tts-qa" element={<Navigate to="/admin/voice-studio?tab=test" replace />} />
            <Route path="voice-studio" element={<LazyLoadWrapper fallback="spinner"><AdminVoiceStudio /></LazyLoadWrapper>} />
            <Route path="contabilidad" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><AdminContabilidad /></LazyLoadWrapper>} />
            {/* Ruta libro-socios oculta (card 140): backend v3.3 aún no en prod */}
            {/* PiliLab — portado desde Desarrollo_old3 (UI más completa: record/voices/simulate/eval) */}
            <Route path="pili-lab" element={<LazyLoadWrapper fallback="spinner"><PiliLabLayout /></LazyLoadWrapper>}>
              <Route index element={<LazyLoadWrapper fallback="spinner"><PiliLabDashboard /></LazyLoadWrapper>} />
              <Route path="record" element={<LazyLoadWrapper fallback="spinner"><PiliLabRecord /></LazyLoadWrapper>} />
              <Route path="voices" element={<LazyLoadWrapper fallback="spinner"><PiliLabVoices /></LazyLoadWrapper>} />
              <Route path="voices/:id" element={<LazyLoadWrapper fallback="spinner"><PiliLabVoiceDetail /></LazyLoadWrapper>} />
              <Route path="simulate" element={<LazyLoadWrapper fallback="spinner"><PiliLabSimulate /></LazyLoadWrapper>} />
              <Route path="demo-call" element={<LazyLoadWrapper fallback="spinner"><PiliLabDemoCall /></LazyLoadWrapper>} />
              <Route path="prompts" element={<LazyLoadWrapper fallback="spinner"><PiliLabPrompts /></LazyLoadWrapper>} />
              <Route path="evals" element={<LazyLoadWrapper fallback="spinner"><PiliLabEvals /></LazyLoadWrapper>} />
              <Route path="evals/:id" element={<LazyLoadWrapper fallback="spinner"><PiliLabEvalDetail /></LazyLoadWrapper>} />
              <Route path="audit" element={<LazyLoadWrapper fallback="spinner"><PiliLabAudit /></LazyLoadWrapper>} />
              {/* Voice Tabs (sprint 2 fase A — read-only) */}
              <Route path="speeches" element={<LazyLoadWrapper fallback="spinner"><PiliLabSpeeches /></LazyLoadWrapper>} />
              <Route path="rulebook" element={<LazyLoadWrapper fallback="spinner"><PiliLabRulebook /></LazyLoadWrapper>} />
              <Route path="preview" element={<LazyLoadWrapper fallback="spinner"><PiliLabPreview /></LazyLoadWrapper>} />
              <Route path="queue" element={<LazyLoadWrapper fallback="spinner"><PiliLabQueue /></LazyLoadWrapper>} />
              <Route path="patterns" element={<LazyLoadWrapper fallback="spinner"><PiliLabPatterns /></LazyLoadWrapper>} />
            </Route>
            <Route path="opportunities" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><AdminOpportunities /></LazyLoadWrapper>} />
            {/* Automatización · Motor de funnels (orden: rutas literales antes de :id) */}
            <Route path="funnels" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={4}><CRMFunnels /></LazyLoadWrapper>} />
            <Route path="funnels/new" element={<LazyLoadWrapper fallback="spinner"><CRMFunnelEditor /></LazyLoadWrapper>} />
            <Route path="funnels/:id/edit" element={<LazyLoadWrapper fallback="spinner"><CRMFunnelEditor /></LazyLoadWrapper>} />
            <Route path="funnels/:id" element={<LazyLoadWrapper fallback="spinner"><CRMFunnelDetail /></LazyLoadWrapper>} />
            <Route path="release-free-candidates" element={<LazyLoadWrapper fallback="spinner"><AdminReleaseFreeCandidates /></LazyLoadWrapper>} />
            <Route path="stripe-search" element={<LazyLoadWrapper fallback="spinner"><AdminStripeSearch /></LazyLoadWrapper>} />
            {/* Agent Orchestrator — orden: estáticas antes que dinámicas */}
            <Route path="agents" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><AdminAgents /></LazyLoadWrapper>} />
            <Route path="agents/new" element={<LazyLoadWrapper fallback="spinner"><AdminAgentNew /></LazyLoadWrapper>} />
            <Route path="agents/executions" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><AdminAgentsExecutions /></LazyLoadWrapper>} />
            <Route path="agents/executions/:executionId" element={<LazyLoadWrapper fallback="spinner"><AdminExecutionDetail /></LazyLoadWrapper>} />
            <Route path="agents/pipelines" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={3}><AdminAgentsPipelines /></LazyLoadWrapper>} />
            <Route path="agents/pipelines/new" element={<LazyLoadWrapper fallback="spinner"><AdminPipelineNew /></LazyLoadWrapper>} />
            <Route path="agents/pipelines/runs" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><AdminPipelineRuns /></LazyLoadWrapper>} />
            <Route path="agents/pipelines/runs/:runId" element={<LazyLoadWrapper fallback="spinner"><AdminPipelineRunDetail /></LazyLoadWrapper>} />
            <Route path="agents/pipelines/:pipelineId" element={<LazyLoadWrapper fallback="spinner"><AdminPipelineDetail /></LazyLoadWrapper>} />
            <Route path="agents/pipelines/:pipelineId/edit" element={<LazyLoadWrapper fallback="spinner"><AdminPipelineEdit /></LazyLoadWrapper>} />
            <Route path="agents/pipelines/:pipelineId/run" element={<LazyLoadWrapper fallback="spinner"><AdminPipelineRun /></LazyLoadWrapper>} />
            <Route path="agents/:agentId" element={<LazyLoadWrapper fallback="spinner"><AdminAgentDetail /></LazyLoadWrapper>} />
            <Route path="agents/:agentId/edit" element={<LazyLoadWrapper fallback="spinner"><AdminAgentEdit /></LazyLoadWrapper>} />
            <Route path="agents/:agentId/execute" element={<LazyLoadWrapper fallback="spinner"><AdminAgentExecute /></LazyLoadWrapper>} />
            <Route path="agents/:agentId/executions" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><AdminAgentExecutions /></LazyLoadWrapper>} />
            {/* MCP — Pili Meta Ads dashboard + creative generator */}
            <Route path="mcp/ads" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={3}><AdminMCPAds /></LazyLoadWrapper>} />
          </Route>
          
          {/* CRM Onboarding — fuera del CRMLayout para fullscreen */}
          <Route
            path="/crm/onboarding"
            element={
              <ProtectedRoute allowedRoles={['lawyer', 'agent', 'user', 'admin', 'superuser']}>
                <LazyLoadWrapper fallback="spinner"><CRMOnboardingPage /></LazyLoadWrapper>
              </ProtectedRoute>
            }
          />

          {/* Legacy redirect: /settings/google-calendar/callback → /crm/google-calendar/callback */}
          <Route path="/settings/google-calendar/callback" element={<Navigate to="/crm/google-calendar/callback" replace />} />

          {/* CRM Routes with Layout */}
          <Route
            path="/crm"
            element={
              <ProtectedRoute allowedRoles={['lawyer', 'agent', 'user', 'admin', 'superuser']}>
                <CRMLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<LazyLoadWrapper fallback="spinner"><CRMDashboardPage /></LazyLoadWrapper>} />
            <Route path="dashboard" element={<Navigate to="/crm" replace />} />
            
            {/* CRM Contacts (con tabs Contactos / Clientes / Todos via ?tab=) */}
            <Route path="contacts" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><CRMContactList /></LazyLoadWrapper>} />
            <Route path="contacts/new" element={<LazyLoadWrapper fallback="spinner"><CRMContactEdit /></LazyLoadWrapper>} />
            <Route path="contacts/:id/edit" element={<LazyLoadWrapper fallback="spinner"><CRMContactEdit /></LazyLoadWrapper>} />
            <Route path="contacts/:id" element={<LazyLoadWrapper fallback="spinner"><CRMContactDetail /></LazyLoadWrapper>} />
            <Route path="contacts/:id/cliente" element={<LazyLoadWrapper fallback="spinner"><ClienteDetalle /></LazyLoadWrapper>} />

            {/* CRM Contracts - Módulo completo (lista, crear, detalle). Permisos dinámicos para agentes/abogados */}
            <Route 
              path="contracts" 
              element={
                <ProtectedRoute useDynamicPermissions={true}>
                  <LazyLoadWrapper fallback="spinner"><CRMContracts /></LazyLoadWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="contracts/create" 
              element={
                <ProtectedRoute useDynamicPermissions={true}>
                  <LazyLoadWrapper fallback="spinner">
                    <AdminContractCreate contractsListPath="/crm/contracts" />
                  </LazyLoadWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="contracts/:code" 
              element={
                <ProtectedRoute useDynamicPermissions={true}>
                  <LazyLoadWrapper fallback="spinner">
                    <AdminContractDetail contractsListPath="/crm/contracts" />
                  </LazyLoadWrapper>
                </ProtectedRoute>
              } 
            />

            {/* CRM Contratos (new) - Protegido con permisos dinámicos */}
            <Route
              path="contratos-new/crear"
              element={
                <ProtectedRoute useDynamicPermissions={true}>
                  <CRMContractCreateNew />
                </ProtectedRoute>
              }
            />
            <Route
              path="contratos-new/plantillas-anexos"
              element={
                <ProtectedRoute useDynamicPermissions={true}>
                  <CRMAnnexTemplatesNew />
                </ProtectedRoute>
              }
            />
            <Route
              path="contratos-new/:code"
              element={
                <ProtectedRoute useDynamicPermissions={true}>
                  <CRMContractDetailNew />
                </ProtectedRoute>
              }
            />
            <Route
              path="contratos-new"
              element={
                <ProtectedRoute useDynamicPermissions={true}>
                  <CRMContractsListNew />
                </ProtectedRoute>
              }
            />
            
            {/* CRM Leads */}
            <Route path="leads" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><CRMLeadList /></LazyLoadWrapper>} />
            <Route path="leads/:id" element={<LazyLoadWrapper fallback="spinner"><CRMLeadDetail /></LazyLoadWrapper>} />
            
            {/* CRM Opportunities — rutas FIJAS antes de /:id para no ser sombreadas */}
            {/* Home del módulo: inbox de leads sin reclamar (broadcast pendientes). */}
            <Route path="opportunities" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><CRMOpportunitiesInbox /></LazyLoadWrapper>} />
            {/* /opportunities/list: CRUD/listado completo (era el home anterior). */}
            <Route path="opportunities/list" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><CRMOpportunities /></LazyLoadWrapper>} />
            {/* Compat: links viejos a /opportunities/inbox redirigen al home. */}
            <Route path="opportunities/inbox" element={<Navigate to="/crm/opportunities" replace />} />
            <Route path="opportunities/my-claims" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><CRMOpportunitiesMyClaims /></LazyLoadWrapper>} />
            <Route path="opportunities/claim/:token" element={<LazyLoadWrapper fallback="spinner"><CRMOpportunityClaim /></LazyLoadWrapper>} />
            <Route
              path="opportunities/digest-summary"
              element={
                <ProtectedRoute allowedRoles={['admin', 'superuser']}>
                  <LazyLoadWrapper fallback="spinner"><CRMOpportunitiesDigestSummary /></LazyLoadWrapper>
                </ProtectedRoute>
              }
            />
            <Route path="opportunities/:id" element={<LazyLoadWrapper fallback="spinner"><CRMOpportunityDetail /></LazyLoadWrapper>} />
            <Route path="opportunities/:opportunityId/analyze" element={<LazyLoadWrapper fallback="spinner"><CRMCaseAnalysis /></LazyLoadWrapper>} />
            
            {/* CRM Appointments */}
            <Route path="appointments" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><CRMAppointments /></LazyLoadWrapper>} />
            <Route path="appointments/:id" element={<LazyLoadWrapper fallback="spinner"><CRMAppointmentDetail /></LazyLoadWrapper>} />
            
            {/* CRM Calendar - Unified (citas + llamadas + tareas) */}
            <Route path="calendar" element={<LazyLoadWrapper fallback="spinner"><CRMCalendar /></LazyLoadWrapper>} />
            
            {/* CRM Calendar - Tasks (legacy, mantener para compatibilidad) */}
            <Route path="calendar-tasks" element={<LazyLoadWrapper fallback="spinner"><CRMTaskCalendar /></LazyLoadWrapper>} />
            
            {/* CRM Delegations (Admin only) */}
            <Route 
              path="delegations" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <LazyLoadWrapper fallback="spinner"><CRMDelegations /></LazyLoadWrapper>
                </ProtectedRoute>
              } 
            />
            
            {/* CRM Tasks */}
            <Route path="tasks" element={<LazyLoadWrapper fallback="spinner"><CRMTasks /></LazyLoadWrapper>} />
            <Route path="tasks/:id" element={<LazyLoadWrapper fallback="spinner"><CRMTaskDetail /></LazyLoadWrapper>} />
            
            {/* CRM Notes */}
            <Route path="notes" element={<LazyLoadWrapper fallback="spinner"><CRMNotes /></LazyLoadWrapper>} />
            
            {/* CRM Agent Journal */}
            <Route path="journal" element={<LazyLoadWrapper fallback="spinner"><CRMAgentJournal /></LazyLoadWrapper>} />
            
            {/* CRM Billing — Presupuestos */}
            <Route path="presupuestos" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><CRMPresupuestosList /></LazyLoadWrapper>} />
            <Route path="presupuestos/crear" element={<LazyLoadWrapper fallback="spinner"><CRMPresupuestoCreate /></LazyLoadWrapper>} />
            <Route path="presupuestos/:id" element={<LazyLoadWrapper fallback="spinner"><CRMPresupuestoDetail /></LazyLoadWrapper>} />

            {/* CRM Billing — Facturas */}
            <Route path="facturas" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><CRMFacturasList /></LazyLoadWrapper>} />
            <Route path="facturas/crear" element={<LazyLoadWrapper fallback="spinner"><CRMFacturaCreate /></LazyLoadWrapper>} />
            <Route path="facturas/:id" element={<LazyLoadWrapper fallback="spinner"><CRMFacturaDetail /></LazyLoadWrapper>} />

            {/* CRM Actions & Expedientes */}
            <Route path="actions" element={<LazyLoadWrapper fallback="spinner"><CRMActions /></LazyLoadWrapper>} />
            <Route path="activity-center" element={<LazyLoadWrapper fallback="spinner"><CRMActivityCenter /></LazyLoadWrapper>} />
            <Route path="seguimientos" element={<LazyLoadWrapper fallback="spinner"><SeguimientosBoard /></LazyLoadWrapper>} />
            <Route
              path="expedientes"
              element={
                <LazyLoadWrapper fallback="skeleton" skeletonCount={5}>
                  <CRMExpedientesList />
                </LazyLoadWrapper>
              }
            />
            <Route
              path="expedientes/new"
              element={
                <LazyLoadWrapper fallback="spinner">
                  <CRMExpedienteCreate />
                </LazyLoadWrapper>
              }
            />
            {/* v2 (doc-céntrica) es la ÚNICA ficha del módulo de expedientes.
                /v2 se mantiene como alias para enlaces/marcadores existentes. */}
            <Route
              path="expedientes/:id"
              element={
                <LazyLoadWrapper fallback="spinner">
                  <ExpedienteDetailV2 />
                </LazyLoadWrapper>
              }
            />
            <Route
              path="expedientes/:id/v2"
              element={
                <LazyLoadWrapper fallback="spinner">
                  <ExpedienteDetailV2 />
                </LazyLoadWrapper>
              }
            />
            {/* Ruta legacy /ia → redirige a la ficha v2 (única ficha del expediente, card 75). */}
            <Route path="expedientes/:id/ia" element={<ExpedienteIaRedirect />} />
            
            {/* CRM Incidencias */}
            <Route path="incidencias" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><CRMIncidencias /></LazyLoadWrapper>} />
            <Route path="incidencias/dashboard" element={<LazyLoadWrapper fallback="spinner"><CRMIncidenciaDashboard /></LazyLoadWrapper>} />
            <Route path="incidencias/new" element={<LazyLoadWrapper fallback="spinner"><CRMIncidenciaCreate /></LazyLoadWrapper>} />
            <Route path="incidencias/:id" element={<LazyLoadWrapper fallback="spinner"><CRMIncidenciaDetail /></LazyLoadWrapper>} />

            {/* CRM FAQs Admin */}
            <Route path="faqs" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><CRMFAQsAdmin /></LazyLoadWrapper>} />

            {/* CRM Call Handler */}
            <Route path="call" element={<LazyLoadWrapper fallback="spinner"><CRMCallHandler /></LazyLoadWrapper>} />
            <Route path="call-handler" element={<LazyLoadWrapper fallback="spinner"><CRMCallHandler /></LazyLoadWrapper>} />
            {/* CRM Calls Archive (módulo /crm/grabaciones) */}
            <Route path="grabaciones" element={<LazyLoadWrapper fallback="skeleton" skeletonCount={5}><CRMCallsArchive /></LazyLoadWrapper>} />
            <Route path="sesion" element={<LazyLoadWrapper fallback="spinner"><CRMWorkSession /></LazyLoadWrapper>} />

            {/* Dev: test interactivo llamada Telnyx WebRTC */}
            <Route path="test-call" element={<LazyLoadWrapper fallback="spinner"><TelnyxCallTestPage /></LazyLoadWrapper>} />
            <Route path="test-call-dual" element={<LazyLoadWrapper fallback="spinner"><TelnyxDualCallTestPage /></LazyLoadWrapper>} />
            <Route path="test-webrtc-agent" element={<LazyLoadWrapper fallback="spinner"><AgentPanel /></LazyLoadWrapper>} />
            <Route path="test-webrtc-bot" element={<LazyLoadWrapper fallback="spinner"><BotPanel /></LazyLoadWrapper>} />

            {/* CRM History / Activity Center */}
            <Route path="history" element={<LazyLoadWrapper fallback="spinner"><CRMHistoryPage /></LazyLoadWrapper>} />

            {/* CRM Agent Profile */}
            <Route path="profile" element={<LazyLoadWrapper fallback="spinner"><CRMAgentProfile /></LazyLoadWrapper>} />

            {/* Google Calendar OAuth Callback */}
            <Route path="google-calendar/callback" element={<LazyLoadWrapper fallback="spinner"><GoogleCalendarCallback /></LazyLoadWrapper>} />

            {/* CRM Settings */}
            <Route path="settings" element={<LazyLoadWrapper fallback="spinner"><CRMSettings /></LazyLoadWrapper>} />
            <Route path="settings/task-templates" element={<LazyLoadWrapper fallback="spinner"><CRMTaskTemplatesSettings /></LazyLoadWrapper>} />
            <Route path="settings/custom-fields" element={<LazyLoadWrapper fallback="spinner"><CRMCustomFieldsSettings /></LazyLoadWrapper>} />
            <Route path="settings/timezone" element={<LazyLoadWrapper fallback="spinner"><CRMTimezoneSettings /></LazyLoadWrapper>} />
            <Route path="settings/delegations" element={<LazyLoadWrapper fallback="spinner"><CRMDelegationsSettings /></LazyLoadWrapper>} />
            <Route path="settings/pipelines" element={<Navigate to="/crm/opportunities" replace />} />
            <Route path="settings/users" element={<Navigate to="/admin/users" replace />} />
            <Route path="settings/firma" element={<Navigate to="/crm/settings/migro-signature" replace />} />
            <Route 
              path="settings/responsables" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <LazyLoadWrapper fallback="spinner"><CRMResponsablesSettings /></LazyLoadWrapper>
                </ProtectedRoute>
              } 
            />
            <Route path="settings/migro-signature" element={<LazyLoadWrapper fallback="spinner"><CRMMigroSignatureSettings /></LazyLoadWrapper>} />
          </Route>
          
          {/* Client login (futuro) */}
          <Route path="/login" element={<Login />} />
          
      {/* Catch all - 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <AuthPathnameSync>
            <PerformanceMonitor enabled={import.meta.env.DEV} showSlowOnly={true} slowThreshold={3000} />
            <Toaster richColors position="top-right" closeButton toastOptions={{ duration: 4000 }} />
            <AppContent />
          </AuthPathnameSync>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
